// MTG-S1 IRS L1B: point query reale. Non presenta le radiance come sounding T/Td.
const ENDPOINT = 'https://api.weathersicily.it/v1/irs/point';

const state = {
    map: null,
    enabled: false,
    marker: null,
    popup: null,
    abort: null,
};

const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]));

function fmtTime(value) {
    if (!value) return 'n.d.';
    try {
        return new Intl.DateTimeFormat('it-IT', {
            dateStyle: 'short', timeStyle: 'short', timeZone: 'UTC',
        }).format(new Date(value)) + ' UTC';
    } catch {
        return String(value);
    }
}

function markerAt(lon, lat) {
    state.marker?.remove();
    const node = document.createElement('button');
    node.type = 'button';
    node.className = 'ws-irs-marker';
    node.title = 'Pixel MTG-S1 IRS utilizzato';
    node.setAttribute('aria-label', node.title);
    node.textContent = 'IRS';
    state.marker = new maplibregl.Marker({ element: node, anchor: 'center' })
        .setLngLat([lon, lat])
        .addTo(state.map);
}

function loadingPopup(lngLat) {
    state.popup?.remove();
    state.popup = new maplibregl.Popup({
        className: 'ws-irs-popup-shell', maxWidth: '430px', closeOnClick: false,
    })
        .setLngLat(lngLat)
        .setHTML('<article class="ws-irs-popup"><b>MTG-S1 IRS</b><p>Ricerca del pixel più vicino…</p></article>')
        .addTo(state.map);
}

function errorPopup(message) {
    state.popup?.setHTML(`<article class="ws-irs-popup"><b>MTG-S1 IRS</b>
        <p class="ws-irs-popup__error">${esc(message || 'Dato temporaneamente non disponibile.')}</p></article>`);
}

function drawSpectrum(canvas, bands) {
    const ctx = canvas.getContext('2d');
    const ratio = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const width = canvas.clientWidth || 370;
    const height = 150;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, width, height);
    ctx.font = '10px system-ui';
    ctx.strokeStyle = 'rgba(127,140,160,.28)';
    ctx.fillStyle = '#728096';
    ctx.beginPath();
    ctx.moveTo(38, 8); ctx.lineTo(38, 126); ctx.lineTo(width - 8, 126); ctx.stroke();

    const values = bands.flatMap(band => band['radiance_w_m-2_sr-1_m-1'] || [])
        .map(Number).filter(Number.isFinite);
    if (!values.length) return;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = Math.max(1e-12, max - min);
    const allWave = bands.flatMap(band => band['wavenumber_cm-1'] || [])
        .map(Number).filter(Number.isFinite);
    const waveMin = Math.min(...allWave);
    const waveMax = Math.max(...allWave);
    const colours = ['#ef6c00', '#137fc1'];
    bands.forEach((band, bandIndex) => {
        const series = (band['radiance_w_m-2_sr-1_m-1'] || []).map(Number);
        const wave = (band['wavenumber_cm-1'] || []).map(Number);
        if (!series.length || !wave.some(Number.isFinite)) return;
        ctx.strokeStyle = colours[bandIndex] || '#555';
        ctx.lineWidth = 1.35;
        ctx.beginPath();
        series.forEach((value, index) => {
            if (!Number.isFinite(value) || !Number.isFinite(wave[index])) return;
            const x = 38 + ((wave[index] - waveMin) / Math.max(1, waveMax - waveMin)) * (width - 50);
            const y = 119 - ((value - min) / span) * 105;
            if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.stroke();
    });
    ctx.fillText('Radiance', 2, 15);
    ctx.fillText('numero d’onda (cm⁻¹)', Math.max(40, width - 128), 145);
}

function render(data) {
    const pixel = data.pixel || {};
    markerAt(Number(pixel.lon), Number(pixel.lat));
    state.popup?.setLngLat([Number(pixel.lon), Number(pixel.lat)]);
    state.popup?.setHTML(`<article class="ws-irs-popup">
        <header><span>🛰️</span><div><small>MTG-S1 · IRS LEVEL 1B</small><h3>Osservazione spettrale</h3></div></header>
        <dl>
            <div><dt>Osservazione</dt><dd>${esc(fmtTime(data.observation_time))}</dd></div>
            <div><dt>Punto scelto</dt><dd>${Number(data.clicked?.lat).toFixed(3)}°, ${Number(data.clicked?.lon).toFixed(3)}°</dd></div>
            <div><dt>Pixel IRS</dt><dd>${Number(pixel.lat).toFixed(3)}°, ${Number(pixel.lon).toFixed(3)}°</dd></div>
            <div><dt>Distanza</dt><dd>${Number(pixel.distance_km).toFixed(2)} km</dd></div>
            <div><dt>Dwell / pixel</dt><dd>${esc(pixel.dwell)} · ${esc(pixel.row)}/${esc(pixel.column)}</dd></div>
        </dl>
        <canvas class="ws-irs-popup__spectrum" aria-label="Spettro di radiance IRS"></canvas>
        <div class="ws-irs-popup__legend"><i></i> LWIR <i></i> MWIR</div>
        <p class="ws-irs-popup__notice">Radiance satellitare reale. Non è ancora un profilo verticale T/Td.</p>
        <footer>${esc(data.attribution || 'Contains modified EUMETSAT data')}</footer>
    </article>`);
    const canvas = state.popup?.getElement()?.querySelector('.ws-irs-popup__spectrum');
    if (canvas) drawSpectrum(canvas, Array.isArray(data.bands) ? data.bands : []);
}

async function onMapClick(event) {
    if (!state.enabled) return;
    state.abort?.abort();
    state.abort = new AbortController();
    const { lat, lng } = event.lngLat;
    loadingPopup(event.lngLat);
    try {
        const url = new URL(ENDPOINT);
        url.searchParams.set('lat', lat.toFixed(6));
        url.searchParams.set('lon', lng.toFixed(6));
        const response = await fetch(url, {
            signal: state.abort.signal,
            cache: 'no-store',
            headers: { Accept: 'application/json' },
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
            throw new Error(payload?.error?.message || 'Dato IRS non disponibile.');
        }
        render(payload.data);
    } catch (error) {
        if (error?.name !== 'AbortError') errorPopup(error?.message);
    }
}

export function initIrsSounding(map) {
    state.map = map;
}

export function setIrsSoundingEnabled(enabled) {
    state.enabled = !!enabled;
    if (!state.map) return;
    state.map.getCanvas().classList.toggle('ws-irs-selecting', state.enabled);
    state.map.off('click', onMapClick);
    if (state.enabled) {
        state.map.on('click', onMapClick);
    } else {
        state.abort?.abort();
        state.marker?.remove();
        state.popup?.remove();
        state.marker = null;
        state.popup = null;
    }
}
